import React, { useState, useEffect, useMemo } from 'react';
import { Calendar, Clock, MapPin, Music, Star, Phone, ShoppingCart, CreditCard, X, Loader2, User, Mail, AlertTriangle, Sparkles, ArrowRight, Heart } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Helmet } from 'react-helmet-async';
import { CalendarioDisponibilidad } from '../components/CalendarioDisponibilidad';
import { InteractiveGallery } from '../components/InteractiveGallery';
import { supabase } from '../services/supabaseClient';

interface MabelitaLandingProps {
    siteConfig?: any;
    onNavigate?: (page: string) => void;
}

export const MabelitaLanding: React.FC<MabelitaLandingProps> = ({ siteConfig, onNavigate }) => {
    const defaults = siteConfig || {};
    const layout = defaults.theme_layout || 'classic';

    // ============ BOOKING LOGIC (from Singer.tsx) ============
    const [slots, setSlots] = useState<any[]>([]);
    const [selectedSlots, setSelectedSlots] = useState<any[]>([]);
    const [bookingProducts, setBookingProducts] = useState<any[]>([]);
    const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
    const [bookingForm, setBookingForm] = useState({ name: '', email: '', phone: '', location: '', notes: '' });
    const [bookingError, setBookingError] = useState('');
    const [bookingLoading, setBookingLoading] = useState(false);

    useEffect(() => {
        const fetchSlots = async () => {
            const { data, error } = await supabase
                .from('disponibilidad_tunantada')
                .select('*')
                .eq('estado', 'disponible')
                .order('fecha', { ascending: true });
            if (!error && data) setSlots(data);
        };
        const fetchProducts = async () => {
            const { data, error } = await supabase
                .from('productos')
                .select('id, nombre, precio, descripcion, descripcion_html, horas, imagen_url, categoria_id, categorias!inner(tipo)')
                .in('categorias.tipo', ['tunantada', 'eventos'])
                .order('precio', { ascending: true });
            if (!error && data && data.length > 0) {
                setBookingProducts(data);
                setSelectedProductId(data[0].id);
            }
        };
        fetchSlots();
        fetchProducts();
    }, []);

    const selectedProduct = bookingProducts.find(p => p.id === selectedProductId);

    const displaySlots = useMemo(() => {
        const is30Min = selectedProduct?.slug === 'felicitaciones-30min' || (selectedProduct?.nombre && selectedProduct.nombre.toLowerCase().includes('30min'));
        if (!is30Min) return slots;
        const subdivided: any[] = [];
        slots.forEach(slot => {
            const [hIni] = slot.hora_inicio.split(':').map(Number);
            const [hFin] = slot.hora_fin.split(':').map(Number);
            let duration = hFin - hIni;
            if (duration <= 0) duration += 24;
            if (duration >= 2) {
                for (let i = 0; i < duration * 2; i++) {
                    const currentH = hIni + Math.floor(i / 2);
                    const currentM = (i % 2) === 0 ? '00' : '30';
                    const nextH = hIni + Math.floor((i + 1) / 2);
                    const nextM = ((i + 1) % 2) === 0 ? '00' : '30';
                    const formatH = (h: number) => String(h % 24).padStart(2, '0');
                    subdivided.push({
                        ...slot,
                        id: Number(`${slot.id}${i}`),
                        parent_id: slot.id,
                        hora_inicio: `${formatH(currentH)}:${currentM}`,
                        hora_fin: `${formatH(nextH)}:${nextM}`,
                    });
                }
            } else {
                subdivided.push(slot);
            }
        });
        return subdivided;
    }, [slots, selectedProduct]);

    const toggleSlot = (slot: any) => {
        setSelectedSlots(prev => {
            const exists = prev.find(s => s.id === slot.id);
            if (exists) return prev.filter(s => s.id !== slot.id);
            return [...prev, slot];
        });
    };

    const is30MinProduct = selectedProduct?.slug === 'felicitaciones-30min' || (selectedProduct?.nombre && selectedProduct.nombre.toLowerCase().includes('30min'));
    const requiredBlocks = selectedProduct ? Math.ceil((selectedProduct.horas || (is30MinProduct ? 0.5 : 2)) / (is30MinProduct ? 0.5 : 2)) : 1;
    const totalPrice = selectedProduct ? Number(selectedProduct.precio) : 150;
    const totalHours = selectedSlots.length * (is30MinProduct ? 0.5 : 2);

    const formatDisplayDate = (dateStr: string) => {
        const d = new Date(dateStr + 'T00:00:00');
        return d.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });
    };

    const handleBookingSubmit = async () => {
        if (!bookingForm.name || !bookingForm.email || !bookingForm.phone) {
            setBookingError('Por favor, completa nombre, email y teléfono.');
            return;
        }
        if (!selectedProduct) {
            setBookingError('Selecciona un tipo de contratación.');
            return;
        }
        if (selectedSlots.length < requiredBlocks) {
            setBookingError(`${selectedProduct.nombre} requiere ${selectedProduct.horas}h (${requiredBlocks} bloque${requiredBlocks > 1 ? 's' : ''} de 2h). Selecciona al menos ${requiredBlocks}.`);
            return;
        }
        setBookingLoading(true);
        setBookingError('');
        try {
            const { data, error } = await supabase.functions.invoke('booking-checkout', {
                body: {
                    slot_ids: selectedSlots.map(s => s.id),
                    customer_name: bookingForm.name,
                    customer_email: bookingForm.email,
                    customer_phone: bookingForm.phone,
                    event_type: selectedProduct?.nombre || 'Contratación',
                    product_id: selectedProductId,
                    total_override: totalPrice,
                    notes: `${bookingForm.location ? 'Ubicación: ' + bookingForm.location + '. ' : ''}${bookingForm.notes}`,
                    successUrl: `${window.location.origin}/#booking-success`,
                    cancelUrl: `${window.location.origin}/#contratar`,
                }
            });
            if (error) throw new Error(error.message || 'Error al crear la sesión de pago');
            if (data?.error) throw new Error(data.error);
            if (data?.url) {
                window.location.href = data.url;
            } else {
                throw new Error('No se recibió URL de pago');
            }
        } catch (err: any) {
            setBookingError(err.message || 'Error procesando el pago');
            setBookingLoading(false);
        }
    };

    // Group selected slots by date
    const selectedByDate: Record<string, any[]> = {};
    selectedSlots.forEach(s => {
        if (!selectedByDate[s.fecha]) selectedByDate[s.fecha] = [];
        selectedByDate[s.fecha].push(s);
    });

    // ============ REVIEWS ============
    const defaultReviews = [
        { name: 'María García', text: 'Increíble actuación. Todo el público emocionado.', stars: 5, location: 'Madrid' },
        { name: 'Carlos Quispe', text: 'Auténtico folklore peruano, profesional y puntual.', stars: 5, location: 'Barcelona' },
        { name: 'Festival Inti Raymi', text: 'La mejor artista peruana en Europa.', stars: 5, location: 'París' }
    ];
    const reviews: any[] = Array.isArray(defaults.reviews)
        ? defaults.reviews
        : (typeof defaults.reviews === 'string' ? (() => { try { return JSON.parse(defaults.reviews); } catch { return defaultReviews; } })() : defaultReviews);

    // ============ HERO DEFAULTS ============
    const isExternalOrGeneric = (url: string) => !url || url.includes('picsum') || url.includes('youtube');
    const heroImage = isExternalOrGeneric(defaults.hero_image) ? "/assets/images/hero_mabelita.jpg" : defaults.hero_image;
    const heroTitle = defaults.hero_title || "Mabelita de la Cruz";
    const heroSubtitle = defaults.hero_subtitle || "";
    const heroQuote = defaults.hero_quote || '"La distancia no borra la identidad. Cada hilo que tejo y cada nota que canto es un puente hacia nuestras raíces."';

    // ============ REPERTOIRE ============
    const defaultRepertorio = [
        "Levántate Corazón", "Mi Ángel Guardián", "Planta del Maguey",
        "Amor de Dos Soles", "Canciones tradicionales de Huancayo", "Huaylash moderno"
    ];
    let repertorio = defaultRepertorio;
    try {
        if (typeof defaults.repertorio === 'string') {
            repertorio = JSON.parse(defaults.repertorio);
        } else if (Array.isArray(defaults.repertorio) && defaults.repertorio.length > 0) {
            repertorio = defaults.repertorio;
        }
    } catch {
        repertorio = defaultRepertorio;
    }

    return (
        <div className="min-h-screen bg-white text-gray-900">
            <Helmet>
                <title>Mabelita de la Cruz | Cantante Folklórica & Embajadora Cultural</title>
                <meta name="description" content="Contrata a Mabelita de la Cruz para shows de Tunantada, Huaylash y folklore andino en Europa. Actuaciones en vivo, serenatas y eventos privados." />
            </Helmet>

            {/* ========== SECCIÓN 1: LA ARTISTA ========== */}
            <section id="artista" className="scroll-mt-24">
                {/* Hero */}
                <div className="relative h-[85vh] flex items-center justify-center overflow-hidden">
                    <div className="absolute inset-0 z-0 bg-black">
                        <img src={heroImage} alt="Mabelita de la Cruz" className="w-full h-full object-cover opacity-50" />
                        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/30 to-black/80"></div>
                    </div>
                    <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
                        <h1 className="text-5xl md:text-7xl font-serif font-bold text-white mb-6 drop-shadow-lg tracking-tight leading-tight">
                            {heroTitle}
                        </h1>
                        {heroSubtitle && (
                            <h2 className="text-2xl md:text-3xl font-serif text-gray-100 mb-6 drop-shadow-md">
                                {heroSubtitle}
                            </h2>
                        )}
                        <p className="font-serif italic text-xl md:text-2xl text-gray-200 mb-10 drop-shadow-md max-w-3xl mx-auto">
                            {heroQuote}
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <a href={defaults.hero_cta_primary_link || "#contratar"} className="inline-flex items-center gap-2 bg-mabelita-primary text-white px-8 py-4 rounded-full font-bold text-lg shadow-2xl hover:scale-105 transition-transform">
                                <Calendar size={20} /> {defaults.hero_cta_primary_text || "Contratar Show"}
                            </a>
                            <a href={defaults.hero_cta_secondary_link || "#actuaciones"} className="inline-flex items-center gap-2 bg-white/20 text-white border-2 border-white px-8 py-4 rounded-full font-bold text-lg backdrop-blur-sm hover:bg-white hover:text-mabelita-primary transition-all">
                                <Music size={20} /> {defaults.hero_cta_secondary_text || "Ver Actuaciones"}
                            </a>
                        </div>
                    </div>
                </div>

                {/* Bio + Stats */}
                <div className="py-20 px-6 bg-gradient-to-b from-gray-50 to-white">
                    <div className="max-w-6xl mx-auto">
                        <div className="text-center mb-16">
                            <span className="text-sm font-bold tracking-[0.3em] uppercase text-mabelita-primary mb-4 block">
                                {defaults.section_subtitle || "Artista Peruana en Europa"}
                            </span>
                            <h2 className="text-4xl md:text-5xl font-serif font-bold text-gray-900 mb-6">
                                {defaults.section_title || "La Voz del Valle del Mantaro"}
                            </h2>
                            <p className="text-xl text-gray-600 max-w-3xl mx-auto font-serif italic leading-relaxed whitespace-pre-line">
                                {defaults.singer_bio_text || "Mabelita de la Cruz difunde el arte del Valle del Mantaro en Europa, brillando con la Tunantada, el Huaylash y el Santiago.\nDesde serenatas íntimas para pedidas de mano hasta festivales multitudinarios como las Fiestas Patrias."}
                            </p>
                        </div>

                        {/* Impact Stats */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                            {(Array.isArray(defaults.social_stats) && defaults.social_stats.length > 0 ? defaults.social_stats : [
                                { count: '+50', platform: 'Actuaciones', label: 'En toda Europa' },
                                { count: '+4K', platform: 'Seguidores', label: 'TikTok orgánico' },
                                { count: '5★', platform: 'Valoración', label: 'Calidad garantizada' },
                                { count: '6+', platform: 'Países', label: 'España, Italia, Francia...' }
                            ]).map((stat: any, i: number) => (
                                <div key={i} className="p-6 bg-white rounded-2xl shadow-lg border border-gray-100 hover:-translate-y-1 transition-transform">
                                    <div className="text-4xl font-black text-mabelita-primary mb-2">{stat.count}</div>
                                    <div className="font-bold uppercase tracking-widest text-xs text-gray-800 mb-1">{stat.platform}</div>
                                    <p className="text-xs text-gray-500">{stat.label}</p>
                                </div>
                            ))}
                        </div>

                        {/* Repertoire */}
                        <div className="mt-16 max-w-4xl mx-auto">
                            <h3 className="text-2xl font-serif font-bold text-gray-900 mb-6 text-center flex items-center justify-center gap-3">
                                <Music className="text-mabelita-accent" size={28} /> Repertorio Musical
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {repertorio.map((song, i) => (
                                    <div key={i} className="flex items-center gap-3 text-lg text-gray-700 border-b border-gray-100 pb-3">
                                        <Music className="text-mabelita-accent shrink-0" size={18} />
                                        {song}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ========== SECCIÓN 2: ACTUACIONES (Galería) ========== */}
            <section id="actuaciones" className="py-20 px-6 bg-gradient-to-br from-mabelita-dark to-black text-white scroll-mt-24">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-12">
                        <span className="text-sm font-bold tracking-[0.3em] uppercase text-mabelita-accent mb-4 block">En directo</span>
                        <h2 className="text-4xl md:text-5xl font-serif font-bold mb-4">{defaults.performances_title || 'Mabelita en Acción'}</h2>
                        <p className="text-gray-400 text-lg max-w-2xl mx-auto font-serif italic">
                            {defaults.performances_subtitle || 'Videos y fotos de actuaciones reales en festivales, fiestas y eventos por toda Europa.'}
                        </p>
                    </div>
                    <InteractiveGallery layout={layout} siteConfig={siteConfig} />
                </div>
            </section>

            {/* ========== SECCIÓN 3: CONTRATAR (Calendario + Formulario) ========== */}
            <section id="contratar" className="py-20 px-4 bg-gradient-to-b from-gray-50 to-white scroll-mt-24">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-12">
                        <span className="inline-flex items-center gap-2 text-sm font-bold text-mabelita-primary tracking-widest uppercase mb-3">
                            <Calendar size={16} /> Contratación
                        </span>
                        <h2 className="text-3xl md:text-4xl font-serif font-bold text-gray-900 mb-3">
                            {defaults.contactTitle || 'Contrata tu Show'}
                        </h2>
                        <p className="text-gray-500 text-lg max-w-2xl mx-auto">
                            {defaults.contactText || 'Llevo el folklore peruano a tu evento. Fiestas, bodas, festivales y más.'}. Elige los horarios disponibles, completa tus datos y paga de forma segura.
                        </p>
                    </div>

                    <div className="grid lg:grid-cols-3 gap-8">
                        {/* LEFT: Calendar */}
                        <div className="lg:col-span-2">
                            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
                                <h3 className="font-bold text-lg text-gray-800 mb-4 flex items-center gap-2">
                                    <Calendar size={20} className="text-indigo-600" />
                                    1. Elige tus horarios
                                    {selectedSlots.length > 0 && (
                                        <span className="ml-auto text-sm bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full font-bold">
                                            {selectedSlots.length} × {is30MinProduct ? '30m' : '2h'} = {totalHours}h seleccionadas
                                        </span>
                                    )}
                                </h3>
                                <CalendarioDisponibilidad
                                    slots={displaySlots}
                                    readOnly={true}
                                    onSlotClick={(date, slot) => {
                                        if (slot && slot.estado === 'disponible') {
                                            toggleSlot(slot);
                                        }
                                    }}
                                    selectedSlotIds={selectedSlots.map(s => s.id)}
                                />
                                <p className="text-xs text-gray-400 mt-4 text-center">
                                    Haz click en los huecos verdes para seleccionarlos. Cada bloque = {is30MinProduct ? '30 minutos' : '2 horas'}.
                                </p>
                            </div>
                        </div>

                        {/* RIGHT: Selection summary + Form */}
                        <div className="space-y-4">
                            {/* Selected slots summary */}
                            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-5">
                                <h4 className="font-bold text-sm text-gray-800 mb-3 flex items-center gap-2">
                                    <ShoppingCart size={16} className="text-indigo-600" /> Tu selección
                                </h4>
                                {selectedSlots.length === 0 ? (
                                    <p className="text-sm text-gray-400 italic text-center py-6">Selecciona horarios del calendario</p>
                                ) : (
                                    <div className="space-y-2 max-h-48 overflow-y-auto">
                                        {Object.entries(selectedByDate).map(([date, daySlots]) => (
                                            <div key={date} className="text-sm">
                                                <div className="font-bold text-gray-700 capitalize text-xs">{formatDisplayDate(date)}</div>
                                                {daySlots.sort((a: any, b: any) => { const sk = (t: string) => { const h = parseInt(t || '99', 10); return h < 6 ? String(h + 24).padStart(2, '0') + t.slice(2) : t; }; return sk(a.hora_inicio || '').localeCompare(sk(b.hora_inicio || '')); }).map((s: any) => (
                                                    <div key={s.id} className="flex items-center justify-between py-1 pl-3 border-l-2 border-emerald-400">
                                                        <span className="text-gray-600">
                                                            <Clock size={12} className="inline mr-1" />
                                                            {String(s.hora_inicio).slice(0, 5)} – {String(s.hora_fin).slice(0, 5)}
                                                        </span>
                                                        <button onClick={() => toggleSlot(s)} className="text-red-400 hover:text-red-600 p-1"><X size={14} /></button>
                                                    </div>
                                                ))}
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {selectedSlots.length > 0 && selectedProduct && (
                                    <div className="mt-4 pt-3 border-t border-gray-100">
                                        <div className="text-xs text-gray-500 mb-1">{selectedProduct.nombre} — {selectedSlots.length} bloque{selectedSlots.length > 1 ? 's' : ''} ({totalHours}h)</div>
                                        <div className="flex justify-between text-sm font-bold text-gray-800">
                                            <span>Total</span>
                                            <span className="text-xl text-emerald-600">€{totalPrice}</span>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Booking form */}
                            {selectedSlots.length > 0 && (
                                <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-5 animate-in slide-in-from-bottom-4 duration-300">
                                    <h4 className="font-bold text-sm text-gray-800 mb-3 flex items-center gap-2">
                                        <User size={16} className="text-indigo-600" /> 2. Tus datos
                                    </h4>
                                    <div className="space-y-3">
                                        <div className="relative">
                                            <User size={16} className="absolute top-3 left-3 text-gray-400" />
                                            <input type="text" placeholder="Nombre completo *" value={bookingForm.name}
                                                onChange={e => setBookingForm(f => ({ ...f, name: e.target.value }))}
                                                className="w-full pl-10 p-2.5 rounded-lg border border-gray-200 bg-white text-gray-900 placeholder-gray-400 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
                                        </div>
                                        <div className="relative">
                                            <Mail size={16} className="absolute top-3 left-3 text-gray-400" />
                                            <input type="email" placeholder="Email *" value={bookingForm.email}
                                                onChange={e => setBookingForm(f => ({ ...f, email: e.target.value }))}
                                                className="w-full pl-10 p-2.5 rounded-lg border border-gray-200 bg-white text-gray-900 placeholder-gray-400 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
                                        </div>
                                        <div className="relative">
                                            <Phone size={16} className="absolute top-3 left-3 text-gray-400" />
                                            <input type="tel" placeholder="Teléfono *" value={bookingForm.phone}
                                                onChange={e => setBookingForm(f => ({ ...f, phone: e.target.value }))}
                                                className="w-full pl-10 p-2.5 rounded-lg border border-gray-200 bg-white text-gray-900 placeholder-gray-400 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
                                        </div>
                                        <select value={selectedProductId || ''} onChange={e => setSelectedProductId(Number(e.target.value))}
                                            className="w-full p-2.5 rounded-lg border border-gray-200 bg-white text-gray-900 text-sm focus:ring-2 focus:ring-indigo-500">
                                            {bookingProducts.map(p => (
                                                <option key={p.id} value={p.id}>
                                                    {p.nombre} — €{Number(p.precio).toFixed(0)} ({p.horas}h)
                                                </option>
                                            ))}
                                        </select>
                                        {selectedProduct && (
                                            <div className="text-xs text-gray-500 bg-gray-50 p-4 rounded-xl border border-gray-100 shadow-inner -mt-1">
                                                <div className="prose prose-sm max-w-none prose-p:my-1 prose-strong:text-gray-800" dangerouslySetInnerHTML={{ __html: selectedProduct.descripcion_html || selectedProduct.descripcion }} />
                                                <span className="mt-4 font-bold text-indigo-700 bg-indigo-50 p-2.5 rounded-lg inline-block w-full text-center shadow-sm border border-indigo-100/50">
                                                    ℹ️ Requiere {requiredBlocks} bloque{requiredBlocks > 1 ? 's' : ''} de 2h
                                                    {selectedSlots.length < requiredBlocks && (
                                                        <span className="text-amber-600 block sm:inline mt-1 sm:mt-0"> — ¡Selecciona {requiredBlocks - selectedSlots.length} más arriba!</span>
                                                    )}
                                                </span>
                                            </div>
                                        )}
                                        <div className="relative">
                                            <MapPin size={16} className="absolute top-3 left-3 text-gray-400" />
                                            <input type="text" placeholder="Ubicación del evento" value={bookingForm.location}
                                                onChange={e => setBookingForm(f => ({ ...f, location: e.target.value }))}
                                                className="w-full pl-10 p-2.5 rounded-lg border border-gray-200 bg-white text-gray-900 placeholder-gray-400 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
                                        </div>
                                        <textarea placeholder="Notas adicionales..." rows={2} value={bookingForm.notes}
                                            onChange={e => setBookingForm(f => ({ ...f, notes: e.target.value }))}
                                            className="w-full p-2.5 rounded-lg border border-gray-200 bg-white text-gray-900 placeholder-gray-400 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />

                                        {bookingError && (
                                            <div className="flex items-center gap-2 text-red-600 text-xs bg-red-50 p-2.5 rounded-lg">
                                                <AlertTriangle size={14} /> {bookingError}
                                            </div>
                                        )}

                                        <button onClick={handleBookingSubmit} disabled={bookingLoading}
                                            className="w-full py-3 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm">
                                            {bookingLoading ? (
                                                <><Loader2 size={18} className="animate-spin" /> Procesando...</>
                                            ) : (
                                                <><CreditCard size={18} /> Pagar €{totalPrice} y Reservar</>
                                            )}
                                        </button>
                                        <p className="text-[10px] text-gray-400 text-center">
                                            Pago seguro via Stripe. Al completar la reserva recibirás confirmación por email.
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* ========== SECCIÓN 4: TESTIMONIOS ========== */}
            <section id="testimonios" className="py-20 px-4 bg-[#0d0d0d] text-white scroll-mt-24">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-12">
                        <span className="text-sm font-bold tracking-[0.3em] uppercase text-mabelita-accent mb-4 block">{defaults.testimonials_subtitle || 'Lo que dicen nuestros clientes'}</span>
                        <h2 className="text-4xl md:text-5xl font-serif font-bold mb-4">{defaults.testimonials_title || 'Testimonios'}</h2>
                    </div>
                    <div className="flex items-center gap-4 mb-8 justify-center">
                        <div className="flex items-center gap-1">
                            {[1, 2, 3, 4, 5].map(s => (
                                <div key={s} className="w-7 h-7 rounded-sm bg-[#00b67a] flex items-center justify-center">
                                    <Star size={16} fill="white" className="text-white" />
                                </div>
                            ))}
                        </div>
                        <span className="text-lg font-bold">5.0 — Excelente</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {reviews.slice(0, 3).map((review: any, i: number) => (
                            <div key={i} className="bg-[#1a1a1a] rounded-xl overflow-hidden border border-white/10 hover:border-white/20 transition-all">
                                {review.image_url && (
                                    <div className="aspect-video overflow-hidden">
                                        <img src={review.image_url} alt="" className="w-full h-full object-cover" />
                                    </div>
                                )}
                                <div className="p-6">
                                    <div className="flex items-center gap-0.5 mb-3">
                                        {[1, 2, 3, 4, 5].map(s => (
                                            <div key={s} className={`w-5 h-5 rounded-sm flex items-center justify-center ${s <= (review.stars || 5) ? 'bg-[#00b67a] text-white' : 'bg-gray-700 text-gray-500'}`}>
                                                <Star size={12} fill="currentColor" />
                                            </div>
                                        ))}
                                    </div>
                                    <p className="text-gray-300 text-sm leading-relaxed mb-4 text-left italic">"{review.text}"</p>
                                    <div className="flex items-center gap-3 text-left">
                                        {review.profile_img ? (
                                            <img src={review.profile_img} alt="" className="w-10 h-10 rounded-full object-cover border-2 border-white/20" />
                                        ) : (
                                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-mabelita-primary/30 to-mabelita-accent/30 flex items-center justify-center text-sm font-black text-mabelita-primary">
                                                {(review.name || 'C')[0].toUpperCase()}
                                            </div>
                                        )}
                                        <div>
                                            <div className="font-bold text-sm text-white">{review.name}</div>
                                            {review.location && <div className="text-xs text-gray-500">{review.location}</div>}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>


        </div>
    );
};

export default MabelitaLanding;
